import { Dialog } from "@surya-digital/leo-reactjs-material-ui";
import { observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useUpdateEmailTemplateStore } from "../../../emailTemplates/stores/Hooks";
import { SaveTemplateForm } from "../forms/SaveTemplateForm";
import { UpdateEmailTemplateRPC } from "@pulse/pulse-rpcs/lib/email-cycle/updateEmailTemplateRPC";

interface UpdateCurrentTemplateDialogProps {
  open: boolean;
  closeDialog: (snackbarConfig?: { message: string; error?: boolean }) => void;
  resetIsEmailTemplatesFetchedValue: VoidFunction;
}

export const UpdateCurrentTemplateDialog: React.FC<UpdateCurrentTemplateDialogProps> =
  observer(({ open, closeDialog, resetIsEmailTemplatesFetchedValue }) => {
    const { t } = useTranslation();

    const updateEmailTemplateStore = useUpdateEmailTemplateStore();

    const handleUpdateEmailTemplate = async (): Promise<void> => {
      try {
        await updateEmailTemplateStore.updateEmailTemplate();

        if (updateEmailTemplateStore.emailTemplateName.validationError) return;
        if (updateEmailTemplateStore.updateEmailTemplateRPCError) {
          switch (updateEmailTemplateStore.updateEmailTemplateRPCError) {
            case UpdateEmailTemplateRPC.RPCError.InvalidTemplateId:
              resetIsEmailTemplatesFetchedValue();
              closeDialog({
                message: t(
                  "emailCycles.updateCurrentTemplateInvalidTemplateIdErrorMessage",
                ),
                error: true,
              });
              break;
            case UpdateEmailTemplateRPC.RPCError.ProjectAlreadyArchived:
              closeDialog({
                message: t("common.projectAlreadyArchivedErrorText"),
                error: true,
              });
              break;
            default:
              updateEmailTemplateStore.updateEmailTemplateRPCError satisfies never;
          }
          return;
        }

        closeDialog({
          message: t("emailCycles.updateCurrentTemplateSuccessMessage"),
        });
      } catch {
        closeDialog({
          message: t("emailCycles.updateCurrentTemplateUnexpectedErrorMessage"),
          error: true,
        });
      }
    };

    return (
      <Dialog
        open={open}
        title={t("emailCycles.updateTemplate")}
        primaryButtonText={t("emailCycles.updateTemplate")}
        onPrimaryButtonClick={handleUpdateEmailTemplate}
        isPrimaryButtonDisabled={updateEmailTemplateStore.disableSubmit}
        secondaryButtonText={t("common.cancel")}
        onSecondaryButtonClick={closeDialog}
        isSecondaryButtonDisabled={
          updateEmailTemplateStore.loadingUpdateEmailTemplateRPC
        }
        disableBackdropClick={true}
      >
        <SaveTemplateForm
          templateName={updateEmailTemplateStore.emailTemplateName}
          disableForm={updateEmailTemplateStore.loadingUpdateEmailTemplateRPC}
        />
      </Dialog>
    );
  });
